import { useNavigate } from "react-router-dom";
import { Space, Checkbox, Tooltip, Loader, HoverCard } from "@mantine/core";
import moment from "moment";
import { BsArrowLeft, BsArrowRight } from "react-icons/bs";
import { MdDeleteOutline, MdError, MdRefresh } from "react-icons/md";
import { Pencil, Settings } from "lucide-react";

import {
  EButton,
  ESelect,
  EPageTitle,
  ETextInput,
  ESectionDivider,
  ETable,
  ELabelView,
  EPagination,
  ENumberInput,
} from "libs/ui";
import PageLayout from "PageLayout";
import ESwitch from "libs/ui/src/Switch/ESwitch";
import { EDialog } from "libs/ui/src/Dialog/EDialog";
import EDropzone from "libs/ui/src/EDropZone/EDropzone";

import useAffiliates from "./hook/useAffiliates";

export default function Affiliates() {
  const navigate = useNavigate();

  const {
    allCountryList,
    allCompanyTypes,
    allStateList,
    getAllStateListByCountryId,
    isModalOpen,
    setIsModalOpen,
    formAction,
    setFormAction,
    formStep,
    setFormStep,
    isBtnLoading,
    isLoadingIndex,
    handleClickEdit,
    affiliateForm,
    affiliateList,
    postNewAffiliate,
    isUpdatingStatus,
    updatedAffiliate,
    updatedAffiliateStatus,
    isAgreeWithTerm,
    setIsAgreeWithTerm,
    PANVerification,
    setPANVerification,
    verifyPAN,

    setSearchText,
    searchText,
    // queryParams,
    // setQueryParams,
    isLoading,
    disablePrev,
    disableNext,
    setTriggerNextPage,
    setTriggerPreviousPage,
    paginationQueryParams,
    setPaginationQueryParams,
  } = useAffiliates();

  const formValues = affiliateForm.getValues();

  return (
    <form
      onSubmit={affiliateForm.onSubmit(() => {
        formAction === "add" ? postNewAffiliate() : updatedAffiliate();
      })}
    >
      <PageLayout
        hideFooter={!!formAction}
        footer={
          <div className="flex justify-between items-center">
            {formStep < 2 ? (
              <>
                <div className=" flex gap-3 items-center justify-end w-full">
                  {formAction === "add" ? (
                    <EButton
                      onClick={() => affiliateForm.reset()}
                      variant="outline"
                      leftSection={<MdRefresh />}
                    >
                      {" "}
                      Reset
                    </EButton>
                  ) : (
                    <></>
                  )}
                  <EButton
                    onClick={() => {
                      affiliateForm.reset();
                      setIsModalOpen(false);
                      setFormAction("add");
                    }}
                    variant="outline"
                  >
                    {" "}
                    Cancel{" "}
                  </EButton>
                  <EButton
                    onClick={async () => {
                      const valid = await affiliateForm.validate();
                      console.log("Valid: ", valid);
                      if (valid.hasErrors) {
                        EDialog({
                          title: "Please fill all required fields",
                          children: Object.values(valid.errors).map((e) => (
                            <p className="flex gap-2 items-center">
                              {" "}
                              <MdError color="red" /> {e}
                            </p>
                          )),
                          withoutIcon: true,
                        });
                      } else {
                        setFormStep(formStep + 1);
                      }
                      // affiliateForm.validate() && setFormStep(formStep + 1)
                    }}
                    rightSection={<BsArrowRight />}
                  >
                    Next
                  </EButton>
                </div>
              </>
            ) : (
              <>
                <Checkbox
                  label="I agree to the privacy policy and Terms & Conditions of PML Holidays."
                  checked={isAgreeWithTerm}
                  onChange={(e) => setIsAgreeWithTerm(e.currentTarget.checked)}
                />
                <div className=" flex gap-3 items-center justify-end">
                  <EButton
                    variant="outline"
                    leftSection={<BsArrowLeft />}
                    onClick={() => setFormStep(formStep - 1)}
                  >
                    {" "}
                    Back
                  </EButton>
                  <EButton
                    onClick={() => {
                      affiliateForm.reset();
                      setIsModalOpen(false);
                      setFormAction("add");
                    }}
                    variant="outline"
                  >
                    {" "}
                    Cancel{" "}
                  </EButton>
                  <EButton
                    type="submit"
                    loading={isBtnLoading}
                    disabled={!isAgreeWithTerm}
                    onClick={() => affiliateForm.onSubmit}
                  >
                    {formAction === "add" ? "Save" : "Update"}
                  </EButton>
                </div>
              </>
            )}
          </div>
        }
      >
        {!isModalOpen ? (
          <>
            <EPageTitle
              title="Affiliates"
              subTitle="View and manage all affiliates"
              end={
                <div className="flex gap-2">
                  <EButton onClick={() => setIsModalOpen(!isModalOpen)}>
                    {" Add New"}
                  </EButton>
                </div>
              }
            />

            <Space h="md" />

            {/* SEARCH AFFILIATE FILTER */}
            <div className="mb-5 grid grid-cols-4 gap-5">
              <div>
                <HoverCard openDelay={300} width={400} shadow="md">
                  <HoverCard.Target>
                    <div className="h-10 p-2 bg-blue-50 rounded-md">
                      <span className="text-sm text-primary-600 font-semibold">
                        Search By: {searchText ?? ""}
                      </span>
                    </div>
                  </HoverCard.Target>
                  <HoverCard.Dropdown>
                    <ETextInput
                      autoFocus
                      label="Search Affiliate"
                      description="Search by company name or email"
                      value={searchText ?? undefined}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </HoverCard.Dropdown>
                </HoverCard>
              </div>
            </div>

            {/*TABLE DATA VIEW  */}
            <ETable>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Name</th>
                  {/* <th>Contact Number</th> */}
                  <th>Balance Information</th>
                  <th>Created Date</th>
                  <th>Group</th>
                  <th>Country</th>
                  <th>State & city</th>
                  <th>Status</th>
                  <th></th>
                  {/* <th className="flex justify-end">Manage</th> */}
                </tr>
              </thead>
              <tbody>
                {affiliateList?.map((item, index) => (
                  <tr key={index + "_affiliate"}>
                    <td>
                      <div className="truncate">
                        <div>
                          <div className="font-medium">
                            {item.companyName ?? ""}
                          </div>
                          <div className="opacity-60">
                            {item.companyType
                              ?.toLowerCase()
                              .replaceAll("_", " ")}
                          </div>
                        </div>
                        <div>
                          <ELabelView
                            label="Website"
                            value={item.website ?? ""}
                            horizontal
                          />
                          <ELabelView
                            // className="max-w-[500px] text-wrap"
                            label="Address"
                            value={item.firstAddress ?? ""}
                            horizontal
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="text-sm font-medium">
                          {item.firstName + " " + (item?.lastName ?? "") || ""}{" "}
                          (TY{item.id})
                        </div>
                        <div className="lowercase opacity-60">
                          {item.emailId ?? ""}
                        </div>
                        <div className="lowercase opacity-60">
                          {item.contactNumber ?? ""}
                        </div>
                      </div>
                    </td>
                    {/* <td>{item.mobileNumber ?? "-"}</td> */}
                    <td>
                      <div>
                        <ELabelView
                          label="Remaining Credits"
                          value={
                            item.remainingCredit
                              ? item.remainingCredit + ""
                              : "N/A"
                          }
                          horizontal
                        />
                        <ELabelView
                          label="Credit Limit"
                          value={
                            item.creditLimit ? item.creditLimit + "" : "N/A"
                          }
                          horizontal
                        />
                        <ELabelView
                          label="Validity"
                          value={item.validity ? item.validity + "" : "N/A"}
                          horizontal
                        />
                      </div>
                    </td>
                    <td>{moment(item.createdDate).format("DD/MM/YYYY")}</td>
                    <td>{item.affiliateGroupName ?? "N/A"}</td>
                    <td>{item.countryName ?? ""}</td>
                    <td>
                      <div>
                        {item.stateName ?? ""}, {item.city ?? ""}
                      </div>
                    </td>
                    <td>
                      <div className="mt-3 flex gap-3 items-center">
                        <Tooltip
                          label={
                            item.status === "DELETED" ? "Re Activate" : "Delete"
                          }
                        >
                          <div>
                            <MdDeleteOutline
                              size={20}
                              color={item.status === "DELETED" ? "red" : "grey"}
                              className="cursor-pointer"
                              onClick={() =>
                                updatedAffiliateStatus(
                                  item.id,
                                  item.status === "DELETED"
                                    ? "ACTIVE"
                                    : "DELETED",
                                )
                              }
                            />
                          </div>
                        </Tooltip>
                        <ESwitch
                          checked={item.status === "ACTIVE" ? true : false}
                          readonly={isUpdatingStatus}
                          label={
                            item.status === "ACTIVE" ? "Active" : "In-active"
                          }
                          loading={isLoadingIndex === index}
                          onChange={(e) =>
                            updatedAffiliateStatus(
                              item.id,
                              e ? "ACTIVE" : "INACTIVE",
                            )
                          }
                        />
                        {/* <ESwitch
                              checked={item.status === "DELETED" ? true : false}
                              readonly={isUpdatingStatus}
                              label={item.status === "DELETED" ? "Deleted" : item.status === "ACTIVE" ? "Active" : "In-active"}
                              loading={isLoadingIndex === index}
                              onChange={(e) => updatedAffiliateStatus(item.id, e ? "DELETED" : "ACTIVE")}
                            /> */}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center -space-x-2">
                        <Tooltip label={"Edit"}>
                          <EButton
                            onClick={() => handleClickEdit(item)}
                            size="xs"
                            variant="transparent"
                          >
                            <Pencil size={20} />
                          </EButton>
                        </Tooltip>

                        <Tooltip label={"Manage"}>
                          <EButton
                            onClick={() =>
                              navigate(`/affiliate/manage-affiliates`, {
                                state: item,
                              })
                            }
                            size="xs"
                            variant="transparent"
                          >
                            <Settings size={20} />
                          </EButton>
                        </Tooltip>
                      </div>
                    </td>
                    {/* <td>
                          <ESwitch
                            checked={item.affiliateStatus}
                            label={item.affiliateStatus ? "Active" : "In-active"}
                            loading={isLoadingIndex === index}
                            onChange={(e) => handleChangeStatus(e, index)}
                          />
                        </td> */}
                    {/* <td>
                          <div className="flex flex-col gap-3 items-center">
                            <Tooltip label={"Edit"} >
                              <div>
                                <FiEdit size={16} className="text-green-500 cursor-pointer" onClick={() => handleClickEdit(item)} />
                              </div>
                            </Tooltip>
                            <Tooltip label={"Manage"} >
                              <div>
                                <MdManageAccounts size={26} className="text-sky-500 cursor-pointer" onClick={() => navigate(`/affiliate/manage-affiliates`, { state: item })} />
                              </div>
                            </Tooltip>
                          </div>
                        </td> */}
                  </tr>
                ))}
              </tbody>
            </ETable>

            <EPagination
              isLoading={isLoading}
              disablePrev={disablePrev}
              disableNext={disableNext}
              goToNextPage={() => {
                setPaginationQueryParams({
                  ...paginationQueryParams,
                  date: affiliateList[affiliateList.length - 1].createdDate,
                });
                setTriggerNextPage(true);
              }}
              goToPreviousPage={() => {
                setPaginationQueryParams({
                  ...paginationQueryParams,
                  date: affiliateList[0].createdDate,
                });

                setTriggerPreviousPage(true);
              }}
            />
          </>
        ) : (
          <>
            <EPageTitle
              title={
                formAction === "add" ? "Add New Affiliate" : "Edit Affiliate"
              }
            />

            <div className="grid grid-cols-3 gap-4 px-8 pb-0 pt-8 min-h-screen">
              {formStep === 1 ? (
                <>
                  <ETextInput
                    label="Company Name"
                    placeholder="Enter Name"
                    key={affiliateForm.key("companyName")}
                    {...affiliateForm.getInputProps("companyName")}
                  />
                  <ETextInput
                    label="Website"
                    placeholder="Enter Website Name"
                    key={affiliateForm.key("website")}
                    {...affiliateForm.getInputProps("website")}
                  />
                  <ETextInput
                    label="Address 1"
                    placeholder="Enter Address"
                    key={affiliateForm.key("firstAddress")}
                    {...affiliateForm.getInputProps("firstAddress")}
                  />
                  <ETextInput
                    label="Address 2"
                    placeholder="Enter Address"
                    key={affiliateForm.key("secondAddress")}
                    {...affiliateForm.getInputProps("secondAddress")}
                  />
                  <ESelect
                    searchable
                    label="Country"
                    placeholder="Enter Country"
                    data={allCountryList}
                    key={affiliateForm.key("countryId")}
                    {...affiliateForm.getInputProps("countryId")}
                    onChange={(e) => {
                      if (e) {
                        getAllStateListByCountryId(+e);
                      }
                      affiliateForm.setFieldValue("countryId", e ?? "");
                      affiliateForm.setFieldValue("stateId", null);
                      affiliateForm.setFieldValue("city", "");
                    }}
                  />
                  <ESelect
                    searchable
                    label="State"
                    placeholder="Select"
                    data={allStateList}
                    key={affiliateForm.key("stateId")}
                    {...affiliateForm.getInputProps("stateId")}
                    onChange={(e) =>
                      affiliateForm.setFieldValue("stateId", e ?? "")
                    }
                  />
                  <ETextInput
                    label="City"
                    placeholder="Enter City"
                    key={affiliateForm.key("city")}
                    {...affiliateForm.getInputProps("city")}
                  />
                  <ENumberInput
                    label="Pin Code"
                    placeholder="Enter Pin Code"
                    key={affiliateForm.key("pinCode")}
                    {...affiliateForm.getInputProps("pinCode")}
                  />
                  <ESelect
                    label="Company Type"
                    placeholder="Enter Company Type"
                    data={
                      allCompanyTypes?.length
                        ? allCompanyTypes
                        : [{ label: "Other", value: "Other" }]
                    }
                    key={affiliateForm.key("companyType")}
                    {...affiliateForm.getInputProps("companyType")}
                  />

                  <div></div>
                  <div className="col-span-3">
                    <ESectionDivider title="Basic Settings" />
                  </div>
                  <ESelect
                    label="Title"
                    placeholder="Enter Title"
                    data={[
                      { label: "Mr", value: "MR" },
                      { label: "Miss", value: "MISS" },
                      { label: "Mrs", value: "MRS" },
                    ]}
                    key={affiliateForm.key("title")}
                    {...affiliateForm.getInputProps("title")}
                  />
                  <ETextInput
                    label="First Name"
                    placeholder="Enter First Name"
                    key={affiliateForm.key("firstName")}
                    {...affiliateForm.getInputProps("firstName")}
                  />
                  <ETextInput
                    label="Last Name"
                    placeholder="Enter Last Name"
                    key={affiliateForm.key("lastName")}
                    {...affiliateForm.getInputProps("lastName")}
                  />
                  <ETextInput
                    label="PAN Number"
                    placeholder="Verify your PAN information"
                    // descriptionProps={{ style: { fontSize: "12px" } }}
                    readOnly={PANVerification.isVerifying}
                    rightSection={
                      !PANVerification.PAN ||
                      PANVerification.PAN?.length !== 10 ? (
                        <></>
                      ) : PANVerification.isVerifying ? (
                        <Loader size={16} />
                      ) : (
                        <span
                          className="mr-6 text-sm text-custom-blue cursor-pointer"
                          onClick={verifyPAN}
                        >
                          Verify
                        </span>
                      )
                      // <FaSearch
                      //   className="text-custom-blue cursor-pointer"
                      //   onClick={verifyPAN}
                      // />
                    }
                    value={PANVerification.PAN ?? undefined}
                    onChange={(e) =>
                      setPANVerification({
                        ...PANVerification,
                        PAN: e.target.value,
                      })
                    }
                    // key={affiliateForm.key('panNumber')}
                    // {...affiliateForm.getInputProps('panNumber')}
                  />
                  <ETextInput
                    minLength={12}
                    maxLength={12}
                    label="Aadhar Number"
                    placeholder="Enter Aadhar Number"
                    key={affiliateForm.key("aadhaarNumber")}
                    {...affiliateForm.getInputProps("aadhaarNumber")}
                  />
                  <ETextInput
                    label="TDS Exemption Number"
                    placeholder="Enter TDS Exemption Number"
                    key={affiliateForm.key("tdsExemptionNumber")}
                    {...affiliateForm.getInputProps("tdsExemptionNumber")}
                  />
                  <ENumberInput
                    label="TDS Exemption Limit"
                    placeholder="Enter TDS Exemption Limit"
                    key={affiliateForm.key("tdsExemptionLimit")}
                    {...affiliateForm.getInputProps("tdsExemptionLimit")}
                  />
                  <ETextInput
                    label="TDS Exemption Rate"
                    placeholder="Enter TDS Exemption Rate"
                    key={affiliateForm.key("tdsExemptionRate")}
                    {...affiliateForm.getInputProps("tdsExemptionRate")}
                  />
                  <ETextInput
                    label="TDS Exemption Code"
                    placeholder="Enter TDS Exemption Code"
                    key={affiliateForm.key("tdsExemptionCode")}
                    {...affiliateForm.getInputProps("tdsExemptionCode")}
                  />
                  <ETextInput
                    label="XL Code"
                    placeholder="Enter XL Code"
                    key={affiliateForm.key("xlCode")}
                    {...affiliateForm.getInputProps("xlCode")}
                  />
                  <ENumberInput
                    label="Look to Book Ratio Search"
                    placeholder="Enter Look to Book Ratio Search"
                    key={affiliateForm.key("lookToBookRatioSearch")}
                    {...affiliateForm.getInputProps("lookToBookRatioSearch")}
                  />
                  <ENumberInput
                    label="Look to Book Ratio Book"
                    placeholder="Enter Look to Book Ratio Book"
                    key={affiliateForm.key("lookToBookRatioBook")}
                    {...affiliateForm.getInputProps("lookToBookRatioBook")}
                  />
                  <div></div>

                  <div></div>
                  <div className="col-span-3">
                    <ESectionDivider title="Contact Details" />
                  </div>
                  <ETextInput
                    label="Email"
                    placeholder="Enter Email"
                    key={affiliateForm.key("email")}
                    {...affiliateForm.getInputProps("email")}
                  />
                  <ETextInput
                    label="Contact Number"
                    placeholder="Enter Contact Number"
                    key={affiliateForm.key("contactNumber")}
                    {...affiliateForm.getInputProps("contactNumber")}
                  />
                  {/* <ETextInput
                        label="Mobile Number"
                        placeholder="Enter Mobile Number"
                        key={affiliateForm.key('mobileNumber')}
                        {...affiliateForm.getInputProps('mobileNumber')}
                      /> */}
                  <div></div>

                  {/* <div className="col-span-3 flex gap-8">
                    <Checkbox
                      label="Is Logo Advice"
                      key={affiliateForm.key("isLogoAdvice")}
                      {...affiliateForm.getInputProps("isLogoAdvice", {
                        type: "checkbox",
                      })}
                    />
                    <Checkbox
                      label="Is Corporate"
                      key={affiliateForm.key("isCorporate")}
                      {...affiliateForm.getInputProps("isCorporate", {
                        type: "checkbox",
                      })}
                    />
                    <Checkbox
                      label="Is Supplier Promo Code"
                      key={affiliateForm.key("isSupplierPromoCode")}
                      {...affiliateForm.getInputProps("isSupplierPromoCode", {
                        type: "checkbox",
                      })}
                    />
                    <Checkbox
                      label="PLM Affiliation"
                      key={affiliateForm.key("pmlAffiliate")}
                      {...affiliateForm.getInputProps("pmlAffiliate", {
                        type: "checkbox",
                      })}
                    />
                  </div> */}
                </>
              ) : (
                // DOCUMENT SECTION
                <>
                  <div className="col-span-3">
                    <ESectionDivider title="Documents" />
                  </div>
                  <EDropzone
                    required
                    accept="application/pdf"
                    label="Aadhar Card"
                    helperText="Please upload masked Aadhar card"
                    placeholder="Select Aadhar Card"
                    key={affiliateForm.key("aadhaarCardId")}
                    filesId={
                      formValues.aadhaarCardId ? [formValues.aadhaarCardId] : []
                    }
                    {...affiliateForm.getInputProps("aadhaarCardId")}
                    onChange={(value) =>
                      affiliateForm.setFieldValue("aadhaarCardId", value[0])
                    }
                  />
                  <EDropzone
                    required
                    label="PAN Card ID"
                    placeholder="Select PAN Card"
                    key={affiliateForm.key("panCardId")}
                    filesId={formValues.panCardId ? [formValues.panCardId] : []}
                    {...affiliateForm.getInputProps("panCardId")}
                    onChange={(value) =>
                      affiliateForm.setFieldValue("panCardId", value[0])
                    }
                  />
                  <EDropzone
                    required={
                      formValues.companyType?.toLocaleLowerCase() !==
                      "individual"
                    }
                    label="Company Registration"
                    placeholder="Select Company Registration"
                    key={affiliateForm.key("companyRegistrationId")}
                    filesId={
                      formValues.companyRegistrationId
                        ? [formValues.companyRegistrationId]
                        : []
                    }
                    {...affiliateForm.getInputProps("companyRegistrationId")}
                    onChange={(value) =>
                      affiliateForm.setFieldValue(
                        "companyRegistrationId",
                        value[0],
                      )
                    }
                  />
                  <EDropzone
                    label="GST ID"
                    key={affiliateForm.key("gstId")}
                    filesId={formValues.gstId ? [formValues.gstId] : []}
                    {...affiliateForm.getInputProps("gstId")}
                    onChange={(value) =>
                      affiliateForm.setFieldValue("gstId", value[0])
                    }
                  />
                  <EDropzone
                    label="Driving License"
                    key={affiliateForm.key("drivingLicenseId")}
                    filesId={
                      formValues.drivingLicenseId
                        ? [formValues.drivingLicenseId]
                        : []
                    }
                    {...affiliateForm.getInputProps("drivingLicenseId")}
                    onChange={(value) =>
                      affiliateForm.setFieldValue("drivingLicenseId", value[0])
                    }
                  />
                  <div></div>
                  <ETextInput
                    label="GST Number"
                    placeholder="Enter GST Number"
                    key={affiliateForm.key("gstNumber")}
                    {...affiliateForm.getInputProps("gstNumber")}
                  />
                  <div></div>
                  <div></div>
                  {/* <div className="col-span-3">
                        <ESectionDivider title="Company Logo" />
                      </div> */}
                  <EDropzone
                    label="Company Logo ID"
                    key={affiliateForm.key("companyLogoId")}
                    filesId={
                      formValues.companyLogoId ? [formValues.companyLogoId] : []
                    }
                    {...affiliateForm.getInputProps("companyLogoId")}
                    onChange={(value) =>
                      affiliateForm.setFieldValue("companyLogoId", value[0])
                    }
                  />
                </>
              )}
            </div>

            <Space h={"md"} />
            <Space h={"md"} />

            <div className="sticky bottom-0 flex justify-between items-center bg-neutral-50 border-t border-slate-500 py-4 px-10 ">
              {formStep < 2 ? (
                <>
                  <div className=" flex gap-3 items-center justify-end w-full">
                    {formAction === "add" ? (
                      <EButton
                        variant="outline"
                        leftSection={<MdRefresh />}
                        onClick={() => affiliateForm.reset()}
                      >
                        {" "}
                        Reset
                      </EButton>
                    ) : (
                      <></>
                    )}
                    <EButton
                      onClick={() => {
                        affiliateForm.reset();
                        setIsModalOpen(false);
                        setFormAction("add");
                      }}
                      variant="outline"
                    >
                      {" "}
                      Cancel{" "}
                    </EButton>
                    <EButton
                      rightSection={<BsArrowRight />}
                      onClick={async () => {
                        const valid = await affiliateForm.validate();

                        if (valid.hasErrors) {
                          EDialog({
                            title: "Please fill all required fields",
                            children: Object.values(valid.errors).map((e) => (
                              <p className="flex gap-2 items-center">
                                {" "}
                                <MdError color="red" /> {e}
                              </p>
                            )),
                            withoutIcon: true,
                          });
                        } else {
                          setFormStep(formStep + 1);
                        }
                        // affiliateForm.validate() && setFormStep(formStep + 1)
                      }}
                    >
                      Next
                    </EButton>
                  </div>
                </>
              ) : (
                <>
                  <Checkbox
                    label="I agree to the privacy policy and Terms & Conditions of PML Holidays."
                    checked={isAgreeWithTerm}
                    onChange={(e) =>
                      setIsAgreeWithTerm(e.currentTarget.checked)
                    }
                  />
                  <div className=" flex gap-3 items-center justify-end">
                    <EButton
                      variant="outline"
                      leftSection={<BsArrowLeft />}
                      onClick={() => setFormStep(formStep - 1)}
                    >
                      {" "}
                      Back
                    </EButton>
                    <EButton
                      onClick={() => {
                        affiliateForm.reset();
                        setIsModalOpen(false);
                        setFormAction("add");
                      }}
                      variant="outline"
                    >
                      {" "}
                      Cancel{" "}
                    </EButton>
                    <EButton
                      type="submit"
                      loading={isBtnLoading}
                      disabled={!isAgreeWithTerm}
                    >
                      {formAction === "add" ? "Save" : "Update"}
                    </EButton>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </PageLayout>
    </form>
  );
}
